import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import arrowDown from '../../assets/images/svg/ic-arrow-down-20.svg';
import arrowUp from '../../assets/images/svg/ic-arrow-up-20.svg';
import chevronDownIcon from '../../assets/images/svg/ic-chevron-down.svg';
import chevronUpIcon from '../../assets/images/svg/ic-chevron-up.svg';

import { color } from '../../app/theme';

const useStyles = theme => ({
  dropdown: {
    position: 'relative',
    display: 'flex',
    height: '44px',
    justifyContent: 'flex-start',
    alignItems: 'center',
    border: `1px solid ${color.$10}`,
    padding: '12px 24px',
    borderRadius: '50px',
    WebkitUserSelect: 'none',
    userSelect: 'none',
    cursor: 'pointer',
    backgroundColor: color.$7,
    '&.focus': {
      borderColor: color.$1,
    },
  },
  filterDropdown: {
    display: 'block',
    border: 'none',
    padding: '0px',
    position: 'relative',
    cursor: 'pointer',
  },
  field: {
    ...theme.typography.subtitle1,
    color: color.$13,
    width: '100%',
    lineHeight: 'normal',
  },
  filterField: {
    ...theme.typography.body1,
    color: color.$3,
    lineHeight: 'normal',
  },
  icon: {
    top: 'calc(50% - 12px)',
    right: '8px',
    color: 'rgba(0, 0, 0, 0.3)',
    padding: '0px',
    position: 'absolute',
    pointerEvents: 'none',
    width: '24px',
    height: '24px',
  },
  startIcon: {
    padding: '0px',
    marginRight: '8px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0)',
    },
  },
  menu: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 3,
    backgroundColor: `${color.$7}`,
    boxShadow:
      '0 16px 48px 0 rgba(0, 0, 0, 0.07), 0 6px 18px 0 rgba(0, 0, 0, 0.03)',
    borderRadius: '12px',
    padding: '8px 0',
    margin: 0,
    listStyle: 'none',
    maxHeight: '294px',
    overflowY: 'auto',
    minWidth: '210px',
  },
  item: {
    display: 'flex',
    height: '44px',
    flexWrap: 'wrap',
    alignItems: 'center',
    padding: '13px 24px',
    cursor: 'pointer',
    fontSize: '13px',
    lineHeight: '18px',
    '& > div': {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      display: 'flex',
      alignItems: 'center',
    },
    '&:hover': {
      color: `${color.$2}`,
    },
  },
  selected: {
    color: `${color.$2}`,
  },
  itemDescription: {
    width: '100%',
    color: color.$11,
  },
  contentContainer: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  default: {
    color: color.$10,
  },
});

function Dropdown(props) {
  const {
    classes,
    value,
    defaultValue,
    menuItems,
    name,
    onChange,
    isFilter,
    isIcon,
    style,
    endCount,
    menuClass,
    iconSource,
    isIconEdit,
    className,
    disabled,
    onFocus,
    filterItems,
    placeholder,
    notSetDefaultValue,
  } = props;
  const dropdownRef = useRef(null);
  const [isOpen, setOpen] = useState(false);
  const [dropdownValue, setDropdownValue] = useState(
    value || defaultValue || menuItems?.[0]?.value,
  );
  const [selectValue, setSelectValue] = useState(
    value || defaultValue || menuItems?.[0]?.value,
  );
  const menuItemMap = new Map();

  useEffect(() => {
    if (!notSetDefaultValue) {
      setDropdownValue(value || defaultValue || menuItems?.[0]?.value);
    }
  }, [menuItems]);

  useEffect(() => {
    setDropdownValue(value);
  }, [value]);

  useEffect(() => {
    if (dropdownValue !== value) {
      setDropdownValue(value);
    }
  }, [dropdownValue]);

  const onMenuClick = item => {
    setDropdownValue(item.value);
    setOpen(false);
    if (onChange) {
      onChange(item.value, item);
    }
  };

  const onMouseEnter = itemValue => {
    setSelectValue(itemValue);
  };

  const onMouseOut = () => {
    setSelectValue(value);
  };
  const filterItemValueSet = new Set(filterItems);
  menuItems.forEach(item => {
    menuItemMap.set(item.value, item);
  });
  const menuItemComponents = menuItems
    .filter(item => !filterItemValueSet.has(item.value))
    .map(item => {
      return (
        <li
          className={classnames([
            isFilter && classes.filterField,
            classes.item,
            dropdownValue === item.value ? classes.selected : null,
          ])}
          key={`${name}-item-${item.value}`}
          onMouseEnter={() => onMouseEnter(item.value)}
          onMouseLeave={() => onMouseOut()}
          onClick={() => onMenuClick(item)}
        >
          <div title={item.text}>
            {isIcon && iconSource ? (
              isIconEdit ? (
                <img
                  alt="icon"
                  className={classes.startIcon}
                  src={
                    dropdownValue === item.value || selectValue === item.value
                      ? iconSource[`${item.value}Hover`]
                      : iconSource[item.value]
                  }
                />
              ) : (
                <IconButton className={classes.startIcon}>
                  <img alt="icon" src={iconSource[item.value]} />
                </IconButton>
              )
            ) : null}
            {item.text}
            {item.desc ? (
              <div className={classes.itemDescription}>{item.desc}</div>
            ) : null}
            {endCount && <span style={{ marginLeft: 'auto' }}>{endCount}</span>}
          </div>
        </li>
      );
    });

  const onClickAway = () => {
    setOpen(false);
  };

  const onClick = e => {
    e.preventDefault();
    if (!disabled) {
      setOpen(!isOpen);
    }
    onFocus();
  };

  let menuTop = 0;
  let menuMinWidth = 210;
  if (isOpen) {
    menuTop = dropdownRef.current.offsetHeight + 2;
    menuMinWidth = dropdownRef.current.style.width;
  }
  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div
        className={classnames([
          isFilter ? classes.filterDropdown : classes.dropdown,
          isOpen ? 'focus' : null,
          className,
        ])}
        style={
          disabled
            ? { ...style, border: `solid 1px ${color.$9}`, cursor: 'default' }
            : style
        }
        ref={dropdownRef}
        onClick={onClick}
      >
        <div className={classes.contentContainer}>
          <span
            style={disabled ? { color: color.$10 } : {}}
            className={classnames(
              isFilter ? classes.filterField : classes.field,
              {
                [classes.default]:
                  dropdownValue === undefined || dropdownValue === null,
              },
            )}
          >
            {dropdownValue === undefined || dropdownValue === null
              ? placeholder
              : menuItemMap.get(dropdownValue) &&
                menuItemMap.get(dropdownValue).text}
          </span>
        </div>
        {isOpen ? (
          <IconButton
            className={classes.icon}
            style={isFilter && { right: 'unset' }}
          >
            {isFilter ? (
              <img alt="chevron-up" src={arrowUp} />
            ) : (
              <img alt="chevron-up" src={chevronUpIcon} />
            )}
          </IconButton>
        ) : (
          <IconButton
            className={classes.icon}
            style={isFilter && { right: 'unset' }}
          >
            {isFilter ? (
              <img alt="chevron-down" src={arrowDown} />
            ) : (
              <img alt="chevron-down" src={chevronDownIcon} />
            )}
          </IconButton>
        )}
        {isOpen ? (
          <ul
            className={classnames([classes.menu, menuClass])}
            style={{ top: `${menuTop}px`, minWidth: `${menuMinWidth}` }}
          >
            {menuItemComponents}
          </ul>
        ) : null}
      </div>
    </ClickAwayListener>
  );
}

Dropdown.defaultProps = {
  onFocus: () => {},
  filterItems: [],
  placeholder: '',
  notSetDefaultValue: false,
};

Dropdown.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    }),
  ).isRequired,
  onFocus: PropTypes.func,
  filterItems: PropTypes.array,
  placeholder: PropTypes.string,
  notSetDefaultValue: PropTypes.bool,
};

export default withStyles(useStyles)(Dropdown);
